import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, cld, cldSrcSet, IMG_SIZES } from "../lib/api";
import { Skeleton, SkeletonText, SkeletonRows } from "../components/Skeleton";

const TONES = ["var(--orange)", "var(--blue)", "var(--lime)"];
const MARQUEE_SPEED = 140; // px per second

export default function Home() {
  const [photos, setPhotos] = useState([]);
  const [halftone, setHalftone] = useState(false);
  const [clients, setClients] = useState("");
  const [copy, setCopy] = useState({ homeHeroSub: "", homeRoomsBody: "" });
  const [credits, setCredits] = useState([]);
  const [gear, setGear] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let done = 0;
    const settle = () => {
      done += 1;
      if (done >= 4) setLoading(false);
    };
    api.get("/photos?studio=balboa")
      .then((r) => (r.data?.length ? setPhotos(r.data) : api.get("/photos").then((all) => setPhotos(all.data || []))))
      .catch(() => {})
      .finally(settle);
    api
      .get("/settings")
      .then((r) => {
        setHalftone(!!r.data?.halftoneOn);
        setClients(r.data?.notableClients || "");
        setCopy({ homeHeroSub: r.data?.homeHeroSub || "", homeRoomsBody: r.data?.homeRoomsBody || "" });
      })
      .catch(() => {})
      .finally(settle);
    api.get("/credits").then((r) => setCredits(r.data || [])).catch(() => {}).finally(settle);
    api.get("/gear?studio=balboa").then((r) => setGear(r.data?.items || [])).catch(() => {}).finally(settle);
  }, []);

  const clientList = clients.trim();
  const marqueeText = clientList || "Past clients land here — add them in Admin";
  const recent = credits.slice(0, 3);

  // Measure one copy of the list so the loop distance is exact in every browser
  // (percentage transforms on the track resolve differently in Safari).
  const halfRef = useRef(null);
  const [loop, setLoop] = useState(null);

  useEffect(() => {
    if (loading) return;
    let tries = 0;
    let raf = 0;
    let timer = 0;
    const measure = () => {
      const w = halfRef.current?.getBoundingClientRect().width;
      if (w) {
        setLoop({ w: Math.round(w), dur: (w / MARQUEE_SPEED).toFixed(2) });
        return true;
      }
      return false;
    };
    // The track only mounts once loading flips, so keep trying until the node exists.
    const attempt = () => {
      if (measure() || tries > 30) return;
      tries += 1;
      raf = requestAnimationFrame(attempt);
    };
    attempt();
    timer = setTimeout(measure, 400);
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
    let ro;
    if (window.ResizeObserver && halfRef.current) {
      ro = new ResizeObserver(measure);
      ro.observe(halfRef.current);
    }
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [marqueeText, loading]);

  return (
    <div data-testid="home-page">
      <section className="wrap hero hero-centered" data-testid="hero">
        <div className="hero-badge" data-testid="hero-logo">
          <img
            className="hero-lockup"
            src="https://res.cloudinary.com/nwvsq3gc/image/upload/f_auto,q_auto,w_500/v1786835265/LOGO_1_FULL.png"
            srcSet="https://res.cloudinary.com/nwvsq3gc/image/upload/f_auto,q_auto,w_500/v1786835265/LOGO_1_FULL.png 500w, https://res.cloudinary.com/nwvsq3gc/image/upload/f_auto,q_auto,w_800/v1786835265/LOGO_1_FULL.png 800w"
            sizes="(max-width: 768px) 80vw, 500px"
            alt="Balboa Recording Studio"
            fetchPriority="high"
            decoding="sync"
          />
        </div>
        <h1 className="disp" data-testid="hero-headline">
          Book a Day.
          <br />
          <span className="misreg" data-text="Let's make something.">
            Let's make something.
          </span>
        </h1>
        <p className="measure hero-sub" data-testid="hero-subtext">
          {loading ? <SkeletonText lines={3} last="55%" /> : copy.homeHeroSub}
        </p>
        <div className="hero-cta">
          <Link to="/contact" className="btn" data-testid="hero-book-now">
            Book Now →
          </Link>
        </div>
      </section>

      <section className="marquee-block" data-testid="clients-marquee">
        <p className="label marquee-heading">Who's worked here:</p>
        {loading ? (
          <div style={{ padding: "0 24px" }}>
            <Skeleton h={28} w="100%" />
          </div>
        ) : (
        <div className="marquee">
          <div
            className="marquee-track"
            data-testid="clients-marquee-track"
            style={loop ? { "--mq-w": `${loop.w}px`, animationDuration: `${loop.dur}s` } : undefined}
          >
            <span className="marquee-half" ref={halfRef}>
              {marqueeText}&nbsp;|&nbsp;
            </span>
            <span className="marquee-half" aria-hidden="true">
              {marqueeText}&nbsp;|&nbsp;
            </span>
          </div>
        </div>
        )}
      </section>

      <section className="wrap sec" data-testid="studio-teaser">
        <p className="label" style={{ color: "var(--orange)" }}>01 — The Rooms</p>
        <h2 className="disp h2" style={{ marginTop: 12 }}>Two rooms, one block apart</h2>
        <div className="two-col" style={{ marginTop: 32 }}>
          <p className="measure" data-testid="rooms-teaser-copy">
            {loading ? <SkeletonText lines={4} last="40%" /> : copy.homeRoomsBody}
          </p>
          <div>
            {loading ? (
              <div className="photo-cell sk" />
            ) : photos[0] ? (
              <div className={`${halftone ? "halftone" : "photo-plain"} photo-cell`} style={{ "--tone": TONES[1] }}>
                <img
                  src={cld(photos[0].url, 900)}
                  srcSet={cldSrcSet(photos[0].url)}
                  sizes={IMG_SIZES}
                  alt="Balboa live room"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="photo-cell empty" style={{ display: "grid", placeItems: "center" }}>
                — add photos in Admin —
              </div>
            )}
          </div>
        </div>
        <div style={{ marginTop: 28 }}>
          <Link to="/studio" className="link-arrow" data-testid="studio-teaser-link">
            See the rooms →
          </Link>
        </div>
      </section>

      <section className="wrap sec" data-testid="gear-teaser">
        <p className="label" style={{ color: "var(--orange)" }}>02 — What's Here</p>
        <h2 className="disp h2" style={{ marginTop: 12 }}>Gear you'd actually pick</h2>
        <div className="gear-items" style={{ marginTop: 24 }}>
          {loading && <SkeletonRows rows={8} h={40} testid="skeleton-gear-teaser" />}
          {!loading && (gear.length ? gear.slice(0, 8) : []).map((g) => (
            <div className="gear-item" key={g.id}>
              <span>{g.name}</span>
              <span>{g.model}</span>
            </div>
          ))}
          {!loading && !gear.length && <div className="empty">— nothing here yet —</div>}
        </div>
        <div style={{ marginTop: 28 }}>
          <Link to="/gear" className="link-arrow" data-testid="gear-teaser-link">
            Full list →
          </Link>
        </div>
      </section>

      <section className="wrap sec" data-testid="work-teaser">
        <p className="label" style={{ color: "var(--orange)" }}>03 — Made Here</p>
        <h2 className="disp h2" style={{ marginTop: 12 }}>Recent work</h2>
        <div className="row-list" style={{ marginTop: 24 }}>
          {loading && <SkeletonRows rows={3} h={48} testid="skeleton-recent-work" />}
          {!loading && recent.map((c) => (
            <div className="tape-row mono" key={c.id}>
              <span>{c.artist}</span>
              <span className="only-desk-inline">{c.track}</span>
              <span>{c.year}</span>
            </div>
          ))}
          {!loading && !recent.length && <div className="empty" style={{ marginTop: 8 }}>— add credits in Admin —</div>}
        </div>
        <div style={{ marginTop: 28 }}>
          <Link to="/prev-work" className="link-arrow" data-testid="work-teaser-link">
            All credits →
          </Link>
        </div>
      </section>
    </div>
  );
}
